import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from "@/components/ui/card";
import { normalizeImageUrl } from "@/lib/image-url";
import { formatDistanceToNow } from "date-fns";
import { ko } from "date-fns/locale";
import { Bed, Car, Home, MapPin, MessageCircle, Share2 } from "lucide-react";
import { Link } from "wouter";

export interface RealEstateItem {
  id: string;
  title: string;
  description?: string;
  priceCents?: number | null;
  images?: string[];
  state?: string;
  city?: string;
  suburb?: string;
  // Phase 3.2: 부동산 전용 필드
  bedrooms?: number; // 방개수
  bathrooms?: number; // 화장실 개수
  parking?: boolean; // 주차 가능 여부
  furnished?: boolean; // 가구 여부
  moveInDate?: string; // 입주일
  propertyType?: string; // 렌트/매매/쉐어
  contactMethod?: string;
  phoneNumber?: string;
  kakaoUrl?: string;
  createdAt: string;
  views?: number;
  favorites?: number;
}

interface RealEstateCardProps {
  item: RealEstateItem;
  onInquire?: () => void;
}

export function RealEstateCard({ item, onInquire }: RealEstateCardProps) {
  const formatPrice = () => {
    if (!item.priceCents) return "가격 협의";
    if (item.priceCents === 0) return "무료";
    if (item.priceCents === -1) return "가격 협의";
    return `$${(item.priceCents / 100).toLocaleString()}/주`;
  };

  const timeAgo = formatDistanceToNow(new Date(item.createdAt), {
    addSuffix: true,
    locale: ko,
  });

  return (
    <Link href={`/real-estate/${item.id}`}>
      {/* Phase 3.2: Phase 2 스타일 카드 적용 */}
      <Card className="rounded-xl border-0 shadow-none hover:scale-[1.02] transition-transform overflow-hidden h-full flex flex-col cursor-pointer">
        {/* Image - 16:9 ratio */}
        <div className="relative aspect-video bg-muted overflow-hidden">
          {item.images && item.images.length > 0 ? (
            <img
              src={normalizeImageUrl(item.images[0]) || item.images[0]}
              alt={item.title}
              className="w-full h-full object-cover"
              loading="lazy"
              onError={(e) => {
                e.currentTarget.src =
                  'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="800" height="600"%3E%3Crect fill="%23e5e7eb" width="800" height="600"/%3E%3Ctext fill="%239ca3af" font-family="sans-serif" font-size="32" dy="310" dx="400" text-anchor="middle"%3ENo Image%3C/text%3E%3C/svg%3E';
              }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Home className="w-16 h-16 text-muted-foreground/30" />
            </div>
          )}
          {/* Price overlay */}
          <div className="absolute bottom-2 left-2 bg-background/90 backdrop-blur-sm px-3 py-1 rounded-md">
            <span className="font-bold text-lg">{formatPrice()}</span>
          </div>
        </div>

        <div className="flex flex-col flex-1 p-4">
          {/* Title - font-bold, line-clamp-2 */}
          <CardHeader className="space-y-2 pb-3 p-0">
            <h3 className="font-bold line-clamp-2 text-lg leading-tight">
              {item.title}
            </h3>
          </CardHeader>

          {/* Meta 정보 */}
          <CardContent className="flex-1 space-y-2 p-0">
            {/* Location, TimeAgo */}
            <div className="flex items-center gap-2 flex-wrap text-xs text-muted-foreground">
              {item.suburb && (
                <div className="flex items-center gap-1">
                  <MapPin className="h-3 w-3" />
                  <span>
                    {item.suburb}
                    {item.city ? `, ${item.city}` : ""}
                    {item.state ? `, ${item.state}` : ""}
                  </span>
                </div>
              )}
              <div className="flex items-center gap-1">
                <span>{timeAgo}</span>
              </div>
            </div>

            {/* Phase 3.2: 부동산 전용 메타 - 방개수, 주차, 가구 여부 */}
            <div className="flex items-center gap-2 flex-wrap">
              {item.bedrooms !== undefined && (
                <Badge variant="outline" className="text-xs">
                  <Bed className="h-3 w-3 mr-1" />
                  {item.bedrooms}bed
                </Badge>
              )}
              {item.parking && (
                <Badge variant="outline" className="text-xs">
                  <Car className="h-3 w-3 mr-1" />
                  주차
                </Badge>
              )}
              {item.furnished && (
                <Badge variant="outline" className="text-xs">
                  <Home className="h-3 w-3 mr-1" />
                  가구
                </Badge>
              )}
              {item.propertyType && (
                <Badge variant="outline" className="text-xs">
                  {item.propertyType === "rent"
                    ? "렌트"
                    : item.propertyType === "sale"
                    ? "매매"
                    : "쉐어"}
                </Badge>
              )}
            </div>

            {/* Description */}
            {item.description && (
              <p className="text-sm text-muted-foreground line-clamp-2 mt-2">
                {item.description}
              </p>
            )}

            {/* 입주일 */}
            {item.moveInDate && (
              <div className="text-xs text-muted-foreground mt-1">
                입주일: {new Date(item.moveInDate).toLocaleDateString("ko-KR")}
              </div>
            )}
          </CardContent>

          {/* Footer - CTA: "문의하기" */}
          <CardFooter className="flex items-center justify-between text-sm text-muted-foreground pt-2 p-0 border-t border-line-subtle mt-2">
            <div className="flex items-center gap-3">
              {item.views !== undefined &&
                item.views !== null &&
                item.views > 0 && (
                  <div className="flex items-center gap-1">
                    <span>{item.views.toLocaleString()} views</span>
                  </div>
                )}
            </div>
            <div className="flex items-center gap-2">
              {/* Phase 3.2: 문의하기 버튼 */}
              <Button
                size="sm"
                className="bg-brand-orange hover:bg-brand-orange/90"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (onInquire) {
                    onInquire();
                  } else if (item.kakaoUrl) {
                    window.open(item.kakaoUrl, "_blank");
                  } else {
                    // TODO: 내장 채팅 시작
                    window.location.href = `/chat-start/real-estate/${item.id}`;
                  }
                }}
              >
                <MessageCircle className="h-4 w-4 mr-1" />
                문의하기
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  // TODO: 공유 로직
                }}
              >
                <Share2 className="h-4 w-4" />
              </Button>
            </div>
          </CardFooter>
        </div>
      </Card>
    </Link>
  );
}
